import React, { useMemo, useState } from 'react';
import {
  getCurrentTeamId,
  getTakenContestantIds,
} from '../../utils/draftUtils';
import { normalizeToContestantName } from '../../utils/contestantUtils';
import { getTeamName } from '../../utils/teamUtils';
import { getTribeColor } from '../../utils/tribeUtils';

function TribeBadges({ tribes }) {
  if (!tribes || tribes.length === 0) return null;

  const validTribes = tribes.filter(
    tribe => tribe && tribe.trim() !== '' && tribe !== 'Select tribe...'
  );

  if (validTribes.length === 0) return null;

  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {validTribes.map((tribe, index) => (
          <div
            key={index}
            className="flex-1 h-2 rounded"
            style={{ backgroundColor: getTribeColor(tribe) }}
          />
        ))}
      </div>
    </div>
  );
}

function DraftContestantCard({
  contestant,
  isTaken,
  draftedByTeamName,
  showSelect,
  onSelect,
  selecting,
  isSubmitting,
}) {
  return (
    <div
      className={`relative h-full flex flex-col rounded-lg border-2 p-3 transition-colors ${
        isTaken
          ? 'border-gray-200 bg-gray-100 opacity-60'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="aspect-square mb-3 bg-gray-200 rounded-lg overflow-hidden relative shrink-0">
        {contestant.imageUrl ? (
          <img
            src={contestant.imageUrl}
            alt=""
            className={`absolute inset-x-0 top-0 w-full h-[300%] object-cover object-top rounded-lg pointer-events-none ${
              isTaken ? 'grayscale' : ''
            }`}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-4xl pointer-events-none">
            👤
          </div>
        )}
      </div>
      <h3
        className={`font-medium text-sm truncate ${
          isTaken ? 'line-through text-gray-500' : 'text-gray-900'
        }`}
      >
        {contestant.name}
      </h3>
      {contestant.bio && (
        <p
          className={`text-xs mt-1 line-clamp-2 ${
            isTaken ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          {contestant.bio}
        </p>
      )}
      <TribeBadges tribes={contestant.tribes} />
      {((isTaken && draftedByTeamName) || (showSelect && !isTaken)) && (
        <div className="mt-auto pt-3">
          {isTaken && draftedByTeamName && (
            <p
              className="text-xs text-gray-500 font-medium w-full line-clamp-2"
              title={`Drafted by ${draftedByTeamName}`}
            >
              Drafted by {draftedByTeamName}
            </p>
          )}
          {showSelect && !isTaken && (
            <button
              type="button"
              disabled={selecting}
              onClick={() => onSelect(contestant.id)}
              className="w-full inline-flex justify-center items-center min-h-[44px] px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700 disabled:opacity-50"
            >
              {selecting && isSubmitting ? 'Submitting…' : 'Select'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Live draft room board: pick order, contestants, pick actions.
 */
export default function DraftRoomBoard({
  room,
  rooms = [],
  activeRoomId,
  onRoomSelect,
  canSwitchRooms = false,
  teams = [],
  contestants = [],
  myTeamIds = [],
  isCommissioner = false,
  readOnly = false,
  onPick,
  onUndo,
  picking = false,
  undoing = false,
}) {
  const [selectingId, setSelectingId] = useState('');

  const currentTeamId = getCurrentTeamId(room);
  const takenIds = getTakenContestantIds(room, teams);

  const draftedByTeamId = useMemo(() => {
    const map = {};
    for (const pick of room?.pickHistory || []) {
      if (pick.contestantId) {
        map[pick.contestantId] = pick.teamId;
      }
    }
    return map;
  }, [room?.pickHistory]);

  const sortedContestants = useMemo(
    () =>
      contestants
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [contestants]
  );

  const availableCount = sortedContestants.filter(c => !takenIds.has(c.id))
    .length;

  const isMyTurn =
    currentTeamId &&
    (myTeamIds.includes(currentTeamId) || isCommissioner);
  const canPickNow = Boolean(
    !readOnly &&
      room?.status === 'inProgress' &&
      currentTeamId &&
      isMyTurn
  );

  const handleSelect = async contestantId => {
    if (!contestantId || !currentTeamId || !onPick || picking) return;
    setSelectingId(contestantId);
    try {
      await onPick({
        roomId: room.id,
        teamId: currentTeamId,
        contestantId,
      });
    } finally {
      setSelectingId('');
    }
  };

  if (!room) {
    return (
      <p className="text-sm text-gray-500">Select a draft room to continue.</p>
    );
  }

  const showRoomTabs = rooms.length > 1 && onRoomSelect;

  const roomStatusText =
    room.status === 'complete'
      ? 'This room is complete.'
      : currentTeamId
        ? `On the clock: ${getTeamName(currentTeamId, teams)}`
        : 'Waiting to start…';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          {showRoomTabs ? (
            <div className="space-y-2">
              <div
                className="flex flex-wrap gap-2"
                role="tablist"
                aria-label="Draft rooms"
              >
                {rooms.map(tabRoom => {
                  const isActive = tabRoom.id === (activeRoomId ?? room.id);
                  const isDisabled = !isActive && !canSwitchRooms;
                  const onClock = getCurrentTeamId(tabRoom);
                  const suffix =
                    tabRoom.status === 'complete'
                      ? ' · Done'
                      : onClock && myTeamIds.includes(onClock)
                        ? ' · Your turn'
                        : onClock
                          ? ` · ${getTeamName(onClock, teams)}`
                          : '';
                  return (
                    <button
                      key={tabRoom.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      disabled={isDisabled}
                      onClick={() => {
                        if (canSwitchRooms || isActive) {
                          onRoomSelect(tabRoom.id);
                        }
                      }}
                      className={`min-h-[44px] px-4 py-2 rounded-md text-sm font-medium border ${
                        isActive
                          ? 'bg-survivor-600 text-white border-survivor-600'
                          : isDisabled
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Room {tabRoom.id}
                      {suffix}
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-gray-600">{roomStatusText}</p>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900">
                Draft Room {room.id}
              </h3>
              <p className="text-sm text-gray-600">{roomStatusText}</p>
            </>
          )}
        </div>
        {isCommissioner && onUndo && !readOnly && room.pickHistory?.length > 0 && (
          <button
            type="button"
            onClick={() => onUndo(room.id)}
            disabled={undoing}
            className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
          >
            {undoing ? 'Undoing…' : 'Undo last pick'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            Draft order
          </h4>
          <ol className="space-y-2">
            {(room.teamOrder || []).map((teamId, index) => {
              const pick = room.pickHistory?.find(p => p.teamId === teamId);
              const isCurrent = teamId === currentTeamId;
              const isMine = myTeamIds.includes(teamId);
              return (
                <li
                  key={teamId}
                  className={`flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm ${
                    isCurrent
                      ? 'bg-amber-50 border border-amber-200'
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-x-2 min-w-0">
                    <span className="font-medium text-gray-500 shrink-0">
                      #{index + 1}
                    </span>
                    <span className="font-medium text-gray-900">
                      {getTeamName(teamId, teams)}
                    </span>
                    {isMine && (
                      <span className="text-xs text-survivor-700 font-medium shrink-0">
                        (you)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 min-w-0 ml-4 text-right shrink-0">
                    {pick && (
                      <span className="text-gray-600 truncate">
                        {normalizeToContestantName(
                          pick.contestantId,
                          contestants
                        )}
                      </span>
                    )}
                    {isCurrent && room.status !== 'complete' && (
                      <span className="text-xs font-semibold text-amber-800 shrink-0">
                        Picking
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Contestants
            </h4>
            <p className="text-xs text-gray-500 mt-0.5">
              {availableCount} available
            </p>
          </div>
          {canPickNow && (
            <p className="text-sm text-gray-700 mb-3">
              {isCommissioner && !myTeamIds.includes(currentTeamId)
                ? `Pick for ${getTeamName(currentTeamId, teams)}`
                : 'Select your contestant'}
            </p>
          )}
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {sortedContestants.map(c => {
              const isTaken = takenIds.has(c.id);
              const draftedTeamId = draftedByTeamId[c.id];
              return (
                <li key={c.id} className="h-full">
                  <DraftContestantCard
                    contestant={c}
                    isTaken={isTaken}
                    draftedByTeamName={
                      draftedTeamId
                        ? getTeamName(draftedTeamId, teams)
                        : null
                    }
                    showSelect={canPickNow}
                    onSelect={handleSelect}
                    selecting={picking || selectingId !== ''}
                    isSubmitting={selectingId === c.id}
                  />
                </li>
              );
            })}
            {sortedContestants.length === 0 && (
              <li className="text-sm text-gray-500 col-span-full">
                No contestants in this season.
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
